import {
  eventEmitterDefaultLogger,
  EventEmitterWithLogger,
} from '@surglogs/eventemitter'

import {
  type IAuthTokenStore,
  type AuthTokenStoreEventData,
  AuthTokenStoreEvent,
} from '@surglogs-app/types'

import { decodeJwtToken, isJwtToken } from './helpers'
import { nowInSeconds } from './helpers/nowInSeconds'

export class AuthTokenStore
  extends EventEmitterWithLogger<AuthTokenStoreEvent>
  implements IAuthTokenStore
{
  private token: string | null = null

  private refreshToken: string | null = null

  private expiresIn: number | null = null

  private lastRefresh: number | null = null

  protected logger = eventEmitterDefaultLogger

  protected logPrefix = 'AuthTokenStore'

  setToken = (token: string, refreshToken?: string, expiresIn?: number) => {
    const lastRefresh = nowInSeconds()

    this.token = token
    this.refreshToken = refreshToken ?? null
    this.expiresIn = expiresIn ?? null
    this.lastRefresh = lastRefresh

    this.emit(AuthTokenStoreEvent.TOKEN_UPDATED, {
      token,
      refreshToken,
      expiresIn,
      lastRefresh,
    })
    return this
  }

  clear = () => {
    if (this.token !== null) {
      this.token = null
      this.refreshToken = null
      this.expiresIn = null
      this.lastRefresh = null

      this.emit(AuthTokenStoreEvent.TOKEN_UPDATED, {
        token: null,
        refreshToken: null,
        expiresIn: null,
        lastRefresh: null,
      })
    }

    return this
  }

  getToken = () => {
    return this.token
  }

  getRefreshToken = () => {
    return this.refreshToken
  }

  getTokenMetaData = () => {
    return { expiresIn: this.expiresIn, lastRefresh: this.lastRefresh }
  }

  isTokenValid = () => {
    try {
      const token = this.getToken()
      if (!token) {
        return false
      }

      if (isJwtToken(token)) {
        const decodedToken = decodeJwtToken(token)
        const now = nowInSeconds()

        return decodedToken?.exp ? decodedToken.exp > now : false
      } else {
        // If the token is not a JWT, we assume it's valid if it exists.
        return Boolean(token)
      }
    } catch (error) {
      return false
    }
  }

  isRefreshTokenValid = () => {
    return !!this.getRefreshToken()
  }

  isTokenAboutToExpire = () => {
    const now = nowInSeconds()
    const half = (this.expiresIn ?? 0) / 2
    const when = (this.lastRefresh ?? 0) + half

    return (now ?? 0) >= when
  }

  onChange = (
    listener: (data: AuthTokenStoreEventData) => void,
  ): (() => void) => {
    this.on(AuthTokenStoreEvent.TOKEN_UPDATED, listener)
    return () =>
      this.removeListener(AuthTokenStoreEvent.TOKEN_UPDATED, listener)
  }
}
